import { useCallback, useRef } from "react";

/**
 * Subtle pointer-follow tilt for cards. Writes rotation, pointer position and
 * lift into CSS custom properties so the transforms themselves stay in the
 * stylesheet (.mw-3d, .mw-specular).
 *
 * Only engages for fine pointers (mouse/trackpad) and never when the user has
 * asked for reduced motion, so touch and keyboard users get a plain card.
 *
 * Writes are batched into one animation frame: pointermove fires far more often
 * than the screen refreshes, and setting custom properties per event is what
 * turns a tilt into jank on a page with several live cards.
 */
function useTilt({ max = 6, lift = 0 } = {}) {
  const ref = useRef(null);
  const frame = useRef(0);
  const next = useRef(null);

  const enabled = useCallback(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const flush = useCallback(() => {
    frame.current = 0;
    const node = ref.current;
    const values = next.current;
    if (!node || !values) return;

    node.style.setProperty("--mw-ry", `${values.ry}deg`);
    node.style.setProperty("--mw-rx", `${values.rx}deg`);
    node.style.setProperty("--mw-px", `${values.px}%`);
    node.style.setProperty("--mw-py", `${values.py}%`);
    node.style.setProperty("--mw-lift", `${values.lift}px`);
  }, []);

  const onPointerMove = useCallback(
    (event) => {
      const node = ref.current;
      if (!node || !enabled()) return;

      const rect = node.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      next.current = {
        ry: (x * max).toFixed(2),
        rx: (-y * max).toFixed(2),
        px: ((x + 0.5) * 100).toFixed(1),
        py: ((y + 0.5) * 100).toFixed(1),
        lift: -lift
      };

      if (!frame.current) frame.current = requestAnimationFrame(flush);
    },
    [max, lift, enabled, flush]
  );

  const onPointerLeave = useCallback(() => {
    const node = ref.current;
    if (!node) return;

    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    }
    next.current = null;

    node.style.setProperty("--mw-ry", "0deg");
    node.style.setProperty("--mw-rx", "0deg");
    node.style.setProperty("--mw-lift", "0px");
  }, []);

  return { ref, onPointerMove, onPointerLeave };
}

export default useTilt;
